import Vue from "vue";
import Router from "vue-router";
import Callback from "@/components/auth/Callback";
import NotFound from "@/components/auth/NotFound";
import Forbidden from "@/components/auth/Forbidden";
import TrialExpired from "@/components/auth/TrialExpired";
import RegistrationTokenMissing from "@/components/auth/RegistrationTokenMissing";
import InvalidLoginTicket from "@/components/auth/InvalidLoginTicket";
import MobileOnly from "@/components/auth/MobileOnly";
import EmailVerification from "@/components/auth/EmailVerification";
import AuthRegion from "@/components/auth/AuthRegion";
import AlertUnsubscribe from "@/components/alerts/AlertUnsubscribe";
import UrlError from "@/components/auth/UrlError";
import { getPreferredLandingPage } from "@/services/user/user.service";
import { ASSETS_ROUTE } from "@/components/assetManagement/AssetsSelector.vue";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { EOS_ACTIVE } from "@/common/config";
Vue.use(Router);

let requireAuthFunc;
async function requireAuth(to, from, next) {
  if (!requireAuthFunc) {
    let lib = await import("@/common/authenticate");
    requireAuthFunc = lib.requireAuth;
  }
  return await requireAuthFunc(to, from, next);
}

export default new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      name: "login",
      path: "/login",
      component: () => import("@/components/auth/Login"),
    },
    {
      name: "logout",
      path: "/logout",
      component: () => import("@/components/auth/Logout"),
    },
    {
      name: "landing",
      path: "/",
      component: () => import("@/views/Landing"),
    },
    {
      name: "home",
      path: "/home",
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
    },
    {
      name: "latest",
      path: "/latest",
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
    },
    {
      name: "watch",
      path: "/watch",
      beforeEnter: requireAuth,
      meta: {
        requiresAIAccess: true,
      },
      component: () => import("@/views/Home"),
      children: [
        {
          name: "watch-code",
          path: ":code",
          beforeEnter: requireAuth,
          component: () => import("@/views/Home"),
        },
      ],
    },
    {
      name: "hotspot",
      path: "/hotspot",
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
      children: [
        {
          name: "hotspot-geohash",
          path: ":geohash",
          beforeEnter: requireAuth,
          component: () => import("@/views/Home"),
        },
      ],
    },
    {
      name: "alerts",
      path: "/alerts",
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
      children: [
        {
          name: "alertid",
          path: ":alertid",
          beforeEnter: requireAuth,
          component: () => import("@/views/Home"),
          children: [
            {
              name: "triggeredid",
              path: ":triggeredid",
              beforeEnter: requireAuth,
              component: () => import("@/views/Home"),
            },
          ],
        },
      ],
    },
    {
      name: "search",
      path: "/search",
      alias: "/savedsearch",
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
      children: [
        {
          name: "searchid",
          path: ":searchid",
          beforeEnter: requireAuth,
          component: () => import("@/views/Home"),
        },
      ],
    },
    {
      name: "assets",
      path: ASSETS_ROUTE,
      alias: "/asset", //for backward compatibility
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
      children: [
        {
          name: "assetid",
          path: ":assetid",
          beforeEnter: requireAuth,
          component: () => import("@/views/Home"),
        },
      ],
    },
    {
      name: "grd",
      path: "/grd",
      beforeEnter: requireAuth,
      meta: {
        requiresGrdAccess: true,
      },
      component: () => import("@/components/grd/Grd"),
      children: [
        {
          name: "grd-aboutus",
          path: "aboutus",
          beforeEnter: requireAuth,
          component: () => import("@/components/grd/Grd"),
        },
        {
          name: "grd-countries",
          path: "countries",
          beforeEnter: requireAuth,
          component: () => import("@/components/grd/Grd"),
        },
        {
          name: "grd-reporting",
          path: "reporting",
          beforeEnter: requireAuth,
          component: () => import("@/components/grd/Grd"),
        },
      ],
    },
    {
      name: "eos",
      path: `/${DETAIL_SIDEBAR_TYPES.content}`,
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
      children: EOS_ACTIVE
        ? [
            {
              name: "breaking-event-by-id",
              path: "breaking-events/:eventid",
              beforeEnter: requireAuth,
            },
            {
              name: "future-event-by-id",
              path: "future-events/:eventid",
              beforeEnter: requireAuth,
            },
            {
              name: "maritime-verified-event-by-id",
              path: "verified-events/maritime/:eventid",
              beforeEnter: requireAuth,
            },
            {
              name: "risk-rating-change-by-id",
              path: "risk-ratings/changes/:riskratingchangeid",
              beforeEnter: requireAuth,
            },
            {
              name: "verified-event-by-id",
              path: "verified-events/security/:verifiedeventid",
              beforeEnter: requireAuth,
            },
            {
              name: "evacuation-change-by-id",
              path: "evacuations/changes/:evacuationchangeid",
              beforeEnter: requireAuth,
            },
            {
              name: "analysis-by-id",
              path: "analysis/:analysisid",
              beforeEnter: requireAuth,
            },
            {
              name: "news-by-id", // a.k.a. EventsAI
              path: "news/:newsid",
              beforeEnter: requireAuth,
            },
            {
              name: "social-by-id", // a.k.a. EventsAI Social
              path: "social/:socialid",
              beforeEnter: requireAuth,
            },
            {
              name: "travel-by-id",
              path: "travel/:travelid",
              beforeEnter: requireAuth,
            },
            {
              name: "discover-headline-by-id", // a.k.a. DiscoverAI
              path: "discover/:headlineid",
              beforeEnter: requireAuth,
            },
          ]
        : [],
    },
    {
      name: "map-controls",
      path: "/map",
      beforeEnter: requireAuth,
      component: () => import("@/views/Home"),
      children: [
        {
          name: "map-library",
          path: "library",
          beforeEnter: requireAuth,
          component: () => import("@/views/Home"),
        },
      ],
    },
    {
      path: "/callback",
      component: Callback,
    },
    {
      name: "404",
      path: "/404",
      component: NotFound,
    },
    {
      name: "401",
      path: "/401",
      component: Forbidden,
    },
    {
      name: "TrialExpired",
      path: "/trial-expired",
      component: TrialExpired,
    },
    {
      name: "RegistrationTokenMissing",
      path: "/registration-token-missing",
      component: RegistrationTokenMissing,
    },
    {
      name: "InvalidLoginTicket",
      path: "/invalid-login-ticket",
      component: InvalidLoginTicket,
    },
    {
      name: "UrlError",
      path: "/url-error/:message",
      component: UrlError,
      props: true,
    },
    {
      name: "AuthRegion",
      path: "/auth-region",
      component: AuthRegion,
    },
    {
      name: "MobileOnly",
      path: "/mobile-only",
      component: MobileOnly,
    },
    {
      name: "EmailVerification",
      path: "/verification",
      component: EmailVerification,
    },
    {
      name: "AlertUnsubscribe",
      path: "/unsubscribe/alert/:token",
      component: AlertUnsubscribe,
    },
    {
      path: "*",
      redirect: "/" + getPreferredLandingPage(),
    },
  ],
});
